import { useState, useMemo } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useGA, useUpdaterChainId, useIsValidWETHNetwork } from '~/hooks'
import { StyledPipe, StyledTab, StyledTabs } from '../Tabs'
import { ToolIcon } from '~/components/icons'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { useToken } from '~/hooks/useToken'
import { useLimitSwapPair, useLimitSwapTokens } from '~/state/dex/hooks'
import { useNativeTokenBalance, useTokenBalance } from '~/state/wallet/hooks'
import Tooltip from '~/components/Tooltip'
import { WETH as WETHAddress } from '~/constants/addresses'
import { motion } from 'framer-motion'
import { useTabsConfig } from './hooks'
import NewTag from '~/components/NewTag'
import { useLocalStorage } from '~/hooks/useLocalStorage'

export const ExchangeTab: React.FC<{
  controls?: React.ReactNode
  tipVisible?: boolean
}> = ({ controls, tipVisible }) => {
  const { t } = useTranslation()
  const router = useRouter()
  const isStaking = router.pathname === '/staking'
  const toggleTool = useModalToggle(ApplicationModal.TOOL)
  const { trackElClickCurring } = useGA()

  const chainId = useUpdaterChainId()
  const [inputToken] = useLimitSwapPair()
  const limitSwapTokens = useLimitSwapTokens(chainId)
  const queryAddress = WETHAddress[chainId]
  const WETHOnChain = useToken(queryAddress)
  const WETH = useMemo(() => {
    return WETHOnChain ? WETHOnChain : limitSwapTokens[queryAddress]
  }, [WETHOnChain, limitSwapTokens])

  const ethBalance = useNativeTokenBalance()
  const wethBalance = useTokenBalance(WETH)

  const loTipVisible = useMemo(() => {
    const visible =
      tipVisible &&
      ethBalance &&
      wethBalance &&
      inputToken?.address.toLocaleLowerCase() ===
        WETH?.address.toLocaleLowerCase()
    return visible
  }, [tipVisible, ethBalance, wethBalance, inputToken?.address])

  const showToolBtn = useIsValidWETHNetwork() && !isStaking

  const tabsConfig = useTabsConfig()
  const newTagId = 'new-tag-staking-20250709-visited'
  const [cache, setCache] = useLocalStorage(newTagId, false)
  const [visible, setVisible] = useState(!cache)

  const handleNewTagVisible = () => {
    setVisible(false)
    setCache(true)
  }

  return (
    <StyledTabs>
      {tabsConfig.map((tab, index) => (
        <StyledTabContainer key={tab.path}>
          <StyledTab
            active={tab.isActive}
            data-trackid={tab.trackId}
            onClick={(e) => trackElClickCurring(e)(handleNewTagVisible)}
          >
            <Link href={tab.path}>{t(tab.translationKey)}</Link>
          </StyledTab>
          {tab.showNew && visible && <NewTag style={{ marginLeft: 4 }} />}
          {index < tabsConfig.length - 1 && <StyledPipe />}
        </StyledTabContainer>
      ))}
      <StyledControls>
        {showToolBtn && (
          <Tooltip
            overlayClassName="spot-swap-settings-tooltip lo-wrap-eth"
            arrowPointAtCenter={true}
            placement="bottom"
            title={t('lo_wrap_eth_tip')}
            trigger="click"
            visible={loTipVisible}
          >
            <motion.div
              initial={{ scale: loTipVisible ? 0 : 1 }}
              animate={{ rotate: loTipVisible ? 360 : 0, scale: 1 }}
              transition={{
                type: 'spring',
              }}
            >
              <StyledIconTool
                data-trackid="webtl_instant_tool"
                onClick={(e) => trackElClickCurring(e)(toggleTool)}
              />
            </motion.div>
          </Tooltip>
        )}
        {controls}
      </StyledControls>
    </StyledTabs>
  )
}

const StyledControls = styled.div`
  position: absolute;
  right: 15px;
  top: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;

  .lon-tooltip {
    height: 32px;
  }
`
const StyledIconTool = styled(ToolIcon)`
  cursor: pointer;
`

const StyledTabContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`
